import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormGroup, Validators } from '@angular/forms';
import { FBFormFieldModel } from 'src/app/models/formBuilderModels';

export interface I9DocumentOption {
  label: string;
  list: 'A' | 'B' | 'C';
  titles: string[];
  receipt?: 'replacement' | 'specific';
  legacy?: boolean;
}

export interface I9ListAReplacementChoice {
  label: string;
  value: string;
  documentTitle: string;
  titles: string[];
  receiptRow: number;
}

@Component({
  selector: 'app-i9-document-selector',
  templateUrl: './i9-document-selector.component.html',
  standalone: false
})
export class I9DocumentSelectorComponent implements OnInit {
  @Input() form: UntypedFormGroup;
  @Input() fields: FBFormFieldModel[] = [];

  primaryDocument: I9DocumentOption;
  listCDocument: I9DocumentOption;
  replacementDocumentTitle: string;
  listAReplacementDocumentSelection: string;
  listCReplacementDocumentTitle: string;
  legacyListARows: number[] = [];
  private savedListAReplacementChoice: I9ListAReplacementChoice;

  readonly documentOptions: I9DocumentOption[] = [
    { label: 'List A — U.S. Passport or U.S. Passport Card', list: 'A', titles: ['U.S. Passport or U.S. Passport Card'] },
    { label: 'List A — Permanent Resident Card or Alien Registration Receipt Card (Form I-551)', list: 'A', titles: ['Permanent Resident Card or Alien Registration Receipt Card (Form I-551)'] },
    { label: 'List A — Foreign passport with temporary I-551 stamp or notation', list: 'A', titles: ['Foreign passport with temporary I-551 stamp or notation'] },
    { label: 'List A — Employment Authorization Document with photograph (Form I-766)', list: 'A', titles: ['Employment Authorization Document (Form I-766)'] },
    { label: 'List A — Foreign passport and Form I-94/I-94A', list: 'A', titles: ['Foreign passport', 'Form I-94 or Form I-94A'] },
    { label: 'List A — FSM or RMI passport and Form I-94/I-94A', list: 'A', titles: ['FSM or RMI passport', 'Form I-94 or Form I-94A'] },
    { label: 'List A receipt — Replacement of a lost, stolen, or damaged List A document', list: 'A', titles: [], receipt: 'replacement' },
    { label: 'List A receipt — Form I-94/I-94A with temporary I-551 stamp and photograph', list: 'A', titles: ['Form I-94 or Form I-94A with temporary I-551 stamp and photograph'], receipt: 'specific' },
    { label: 'List A receipt — Form I-94/I-94A with refugee admission code', list: 'A', titles: ['Form I-94 or Form I-94A with refugee admission code'], receipt: 'specific' },
    { label: "List B — Driver's license or ID card issued by a state or outlying possession", list: 'B', titles: ["Driver's license or state ID card"] },
    { label: 'List B — Federal, state, or local government ID card', list: 'B', titles: ['Federal, state, or local government ID card'] },
    { label: 'List B — School ID card with photograph', list: 'B', titles: ['School ID card with photograph'] },
    { label: "List B — Voter's registration card", list: 'B', titles: ["Voter's registration card"] },
    { label: 'List B — U.S. military card or draft record', list: 'B', titles: ['U.S. military card or draft record'] },
    { label: "List B — Military dependent's ID card", list: 'B', titles: ["Military dependent's ID card"] },
    { label: 'List B — U.S. Coast Guard Merchant Mariner Card', list: 'B', titles: ['U.S. Coast Guard Merchant Mariner Card'] },
    { label: 'List B — Native American tribal document', list: 'B', titles: ['Native American tribal document'] },
    { label: "List B — Canadian driver's license", list: 'B', titles: ["Canadian driver's license"] },
    { label: 'List B — School record or report card (individual under 18)', list: 'B', titles: ['School record or report card'] },
    { label: 'List B — Clinic, doctor, or hospital record (individual under 18)', list: 'B', titles: ['Clinic, doctor, or hospital record'] },
    { label: 'List B — Day-care or nursery school record (individual under 18)', list: 'B', titles: ['Day-care or nursery school record'] },
    { label: 'List B receipt — Replacement of a lost, stolen, or damaged List B document', list: 'B', titles: [], receipt: 'replacement' },
    { label: 'List C — Unrestricted Social Security account number card', list: 'C', titles: ['Unrestricted Social Security account number card'] },
    { label: 'List C — Certification of Birth Abroad (Forms DS-1350, FS-545, or FS-240)', list: 'C', titles: ['Certification of Birth Abroad'] },
    { label: 'List C — Original or certified copy of a U.S. birth certificate', list: 'C', titles: ['Original or certified copy of a U.S. birth certificate'] },
    { label: 'List C — Native American tribal document', list: 'C', titles: ['Native American tribal document'] },
    { label: 'List C — U.S. Citizen ID Card (Form I-197)', list: 'C', titles: ['U.S. Citizen ID Card (Form I-197)'] },
    { label: 'List C — Identification Card for Use of Resident Citizen (Form I-179)', list: 'C', titles: ['Identification Card for Use of Resident Citizen (Form I-179)'] },
    { label: 'List C — Employment authorization document issued by DHS', list: 'C', titles: ['Employment authorization document issued by DHS'] },
    { label: 'List C receipt — Replacement of a lost, stolen, or damaged List C document', list: 'C', titles: [], receipt: 'replacement' }
  ];

  readonly statesAndTerritories = [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
    'District of Columbia', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
    'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota',
    'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey',
    'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon',
    'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah',
    'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming', 'American Samoa',
    'Guam', 'Northern Mariana Islands', 'Puerto Rico', 'U.S. Virgin Islands'
  ];

  readonly canadianAuthorities = [
    'Alberta', 'British Columbia', 'Manitoba', 'New Brunswick', 'Newfoundland and Labrador',
    'Northwest Territories', 'Nova Scotia', 'Nunavut', 'Ontario', 'Prince Edward Island',
    'Quebec', 'Saskatchewan', 'Yukon'
  ];

  ngOnInit(): void {
    const listATitle = this.value('A1DocumentTitle');
    const listBTitle = this.value('B1DocumentTitle');
    const listCTitle = this.value('C1DocumentTitle');

    if (this.nonEmptyRows('A', 3).length > 0) {
      if (!this.initializeSavedListAReplacementDocument()) {
        this.primaryDocument = this.findKnownSavedOption('A', 3);
        if (!this.primaryDocument) {
          this.legacyListARows = this.nonEmptyRows('A', 3);
          this.primaryDocument = this.createLegacyOption('A', listATitle);
        }
      }
    } else if (listBTitle) {
      const replacementTitle = this.getSavedReplacementDocumentTitle('B', listBTitle);
      if (replacementTitle !== null) {
        this.primaryDocument = this.replacementReceiptOption('B');
        this.replacementDocumentTitle = replacementTitle;
        if (!replacementTitle)
          this.setValue('B1DocumentTitle', '');
      } else {
        this.primaryDocument = this.findKnownSavedOption('B', 1) || this.createLegacyOption('B', listBTitle);
      }
      if (listCTitle) {
        const listCReplacementTitle = this.getSavedReplacementDocumentTitle('C', listCTitle);
        if (listCReplacementTitle !== null) {
          this.listCDocument = this.replacementReceiptOption('C');
          this.listCReplacementDocumentTitle = listCReplacementTitle;
          if (!listCReplacementTitle)
            this.setValue('C1DocumentTitle', '');
        } else {
          this.listCDocument = this.findKnownSavedOption('C', 1) || this.createLegacyOption('C', listCTitle);
        }
      }
    }

    this.applyDocumentValidators();
  }

  get primaryDocumentOptions(): I9DocumentOption[] {
    const options = this.documentOptions.filter(x => x.list === 'A' || x.list === 'B');
    return this.primaryDocument?.legacy ? [this.primaryDocument, ...options] : options;
  }

  get listCDocumentOptions(): I9DocumentOption[] {
    const options = this.documentOptions.filter(x => x.list === 'C');
    return this.listCDocument?.legacy ? [this.listCDocument, ...options] : options;
  }

  get usesListA(): boolean {
    return this.primaryDocument?.list === 'A';
  }

  get usesListBAndC(): boolean {
    return this.primaryDocument?.list === 'B';
  }

  get isPrimaryReplacementReceipt(): boolean {
    return this.primaryDocument?.receipt === 'replacement';
  }

  get isListCReplacementReceipt(): boolean {
    return this.listCDocument?.receipt === 'replacement';
  }

  selectPrimaryDocument(option: I9DocumentOption): void {
    this.primaryDocument = option;
    if (option?.legacy) {
      this.applyDocumentValidators();
      return;
    }

    this.legacyListARows = [];
    this.replacementDocumentTitle = null;
    this.listAReplacementDocumentSelection = null;
    this.savedListAReplacementChoice = null;
    if (!option) {
      this.clearRows('A', 3);
      this.clearRows('B', 1);
      this.clearRows('C', 1);
      this.listCDocument = null;
      this.listCReplacementDocumentTitle = null;
    } else if (option.list === 'A') {
      this.setDocumentTitles('A', option.titles, 3);
      this.clearRows('B', 1);
      this.clearRows('C', 1);
      this.listCDocument = null;
      this.listCReplacementDocumentTitle = null;
    } else {
      this.clearRows('A', 3);
      this.setDocumentTitles('B', option.titles, 1);
    }
    this.applyDocumentValidators();
    this.form.markAsDirty();
  }

  selectListCDocument(option: I9DocumentOption): void {
    this.listCDocument = option;
    if (option?.legacy) {
      this.applyDocumentValidators();
      return;
    }

    this.listCReplacementDocumentTitle = null;
    if (option)
      this.setDocumentTitles('C', option.titles, 1);
    else
      this.clearRows('C', 1);
    this.applyDocumentValidators();
    this.form.markAsDirty();
  }

  selectReplacementDocument(documentTitle: string): void {
    this.replacementDocumentTitle = documentTitle;
    if (this.primaryDocument)
      this.setDocumentTitles(this.primaryDocument.list, documentTitle ? [`Receipt - ${documentTitle}`] : [], this.primaryDocument.list === 'A' ? 3 : 1);
    this.applyDocumentValidators();
    this.form.markAsDirty();
  }

  selectListAReplacementDocument(selection: string): void {
    this.listAReplacementDocumentSelection = selection;
    const choice = this.listAReplacementDocumentOptions().find(x => x.value === selection);
    const titles = choice?.titles.map((title, index) =>
      index + 1 === choice.receiptRow ? `Receipt - ${title}` : title) || [];
    this.setDocumentTitles('A', titles, 3);
    this.applyDocumentValidators();
    this.form.markAsDirty();
  }

  selectListCReplacementDocument(documentTitle: string): void {
    this.listCReplacementDocumentTitle = documentTitle;
    this.setDocumentTitles('C', documentTitle ? [`Receipt - ${documentTitle}`] : [], 1);
    this.applyDocumentValidators();
    this.form.markAsDirty();
  }

  replacementDocumentOptions(list: 'A' | 'B' | 'C', selectedTitle: string): { label: string, value: string }[] {
    const titles = this.documentOptions
      .filter(x => x.list === list && !x.receipt)
      .reduce((result, option) => [...result, ...option.titles], [] as string[])
      .filter((title, index, allTitles) => allTitles.indexOf(title) === index);

    if (selectedTitle && !titles.includes(selectedTitle))
      titles.unshift(selectedTitle);

    return titles.map(title => ({ label: title, value: title }));
  }

  listAReplacementDocumentOptions(): I9ListAReplacementChoice[] {
    const choices = this.standardListAReplacementDocumentOptions();
    return this.savedListAReplacementChoice && !choices.some(x => x.value === this.savedListAReplacementChoice.value)
      ? [this.savedListAReplacementChoice, ...choices]
      : choices;
  }

  activeRows(list: 'A' | 'B' | 'C'): number[] {
    if (list === 'A' && this.primaryDocument?.legacy)
      return this.legacyListARows;
    if (list === 'A' && this.usesListA)
      return this.isPrimaryReplacementReceipt
        ? this.selectedListAReplacementChoice()?.titles.map((_, index) => index + 1) || [1]
        : this.primaryDocument.titles.map((_, index) => index + 1);
    if (list === 'B' && this.usesListBAndC)
      return [1];
    if (list === 'C' && this.usesListBAndC && this.listCDocument)
      return [1];
    return [];
  }

  isManualRow(list: 'A' | 'B' | 'C'): boolean {
    if (list === 'C')
      return this.listCDocument?.legacy === true;
    return this.primaryDocument?.list === list && this.primaryDocument?.legacy === true;
  }

  controlName(fieldName: string): string {
    return this.fields.find(x => x.fieldName === fieldName)?.formControlIdentifier;
  }

  authorityOptions(documentTitle: string): { label: string, value: string }[] {
    const title = (documentTitle || '').toLowerCase();
    let authorities: string[] = [];
    if (title.includes("driver's license") || title.includes('state id') || title.includes('birth certificate')) {
      authorities = this.statesAndTerritories;
    } else if (title.includes('canadian')) {
      authorities = this.canadianAuthorities;
    } else if (title.includes('u.s. passport') || title.includes('certification of birth abroad')) {
      authorities = ['U.S. Department of State'];
    } else if (title.includes('fsm or rmi passport')) {
      authorities = ['Government of the Federated States of Micronesia', 'Government of the Republic of the Marshall Islands'];
    } else if (title.includes('social security')) {
      authorities = ['Social Security Administration'];
    } else if (title.includes('i-94')) {
      authorities = ['U.S. Customs and Border Protection'];
    } else if (title.includes('i-551') || title.includes('i-766') || title.includes('i-197') || title.includes('i-179')) {
      authorities = ['U.S. Citizenship and Immigration Services'];
    } else if (title.includes('dhs')) {
      authorities = ['U.S. Department of Homeland Security'];
    } else if (title.includes('tribal')) {
      authorities = ['Issuing Native American tribe'];
    }
    return authorities.map(value => ({ label: value, value }));
  }

  private standardListAReplacementDocumentOptions(): I9ListAReplacementChoice[] {
    const choices: I9ListAReplacementChoice[] = [];
    this.documentOptions.forEach((option, optionIndex) => {
      if (option.list !== 'A' || option.receipt)
        return;

      option.titles.forEach((documentTitle, titleIndex) => {
        choices.push({
          label: option.titles.length > 1
            ? `${documentTitle} — ${option.label.replace('List A — ', '')}`
            : documentTitle,
          value: `list-a-${optionIndex}-${titleIndex + 1}`,
          documentTitle,
          titles: [...option.titles],
          receiptRow: titleIndex + 1
        });
      });
    });
    return choices;
  }

  private selectedListAReplacementChoice(): I9ListAReplacementChoice {
    return this.listAReplacementDocumentOptions().find(x => x.value === this.listAReplacementDocumentSelection);
  }

  private initializeSavedListAReplacementDocument(): boolean {
    const savedTitles = [1, 2, 3].map(row => this.value(`A${row}DocumentTitle`));
    const genericReceiptIndex = savedTitles.findIndex(title => title === 'Receipt for replacement of a List A document');
    if (genericReceiptIndex >= 0) {
      this.primaryDocument = this.replacementReceiptOption('A');
      this.listAReplacementDocumentSelection = null;
      this.savedListAReplacementChoice = null;
      this.setValue(`A${genericReceiptIndex + 1}DocumentTitle`, '');
      return true;
    }

    const receiptIndex = savedTitles.findIndex(title => title.startsWith('Receipt - '));
    if (receiptIndex < 0)
      return false;

    const normalizedTitles = savedTitles.map((title, index) =>
      index === receiptIndex ? title.substring('Receipt - '.length) : title);
    const standardChoice = this.standardListAReplacementDocumentOptions().find(choice =>
      choice.receiptRow === receiptIndex + 1
      && normalizedTitles.every((title, index) => title === (choice.titles[index] || '')));
    const lastSavedRowIndex = normalizedTitles.reduce((lastIndex, title, index) =>
      (title || this.rowHasValue('A', index + 1)) ? index : lastIndex, receiptIndex);
    const choice = standardChoice || {
      label: `${normalizedTitles[receiptIndex]} — Saved document combination`,
      value: `saved-list-a-${receiptIndex + 1}-${normalizedTitles[receiptIndex]}`,
      documentTitle: normalizedTitles[receiptIndex],
      titles: normalizedTitles.slice(0, lastSavedRowIndex + 1),
      receiptRow: receiptIndex + 1
    };

    this.primaryDocument = this.replacementReceiptOption('A');
    this.savedListAReplacementChoice = standardChoice ? null : choice;
    this.listAReplacementDocumentSelection = choice.value;
    return true;
  }

  private replacementReceiptOption(list: 'A' | 'B' | 'C'): I9DocumentOption {
    return this.documentOptions.find(x => x.list === list && x.receipt === 'replacement');
  }

  private getSavedReplacementDocumentTitle(list: 'A' | 'B' | 'C', title: string): string | null {
    if (title === `Receipt for replacement of a List ${list} document`)
      return '';
    return title.startsWith('Receipt - ') ? title.substring('Receipt - '.length) : null;
  }

  private findKnownSavedOption(list: 'A' | 'B' | 'C', rowCount: number): I9DocumentOption {
    return this.documentOptions
      .filter(x => x.list === list && x.receipt !== 'replacement')
      .find(option => {
        for (let row = 1; row <= rowCount; row++) {
          const expectedTitle = option.titles[row - 1] || '';
          if (this.value(`${list}${row}DocumentTitle`) !== expectedTitle)
            return false;
          if (!expectedTitle && this.rowHasValue(list, row))
            return false;
        }
        return true;
      });
  }

  private createLegacyOption(list: 'A' | 'B' | 'C', title: string): I9DocumentOption {
    return {
      label: `List ${list} — Manual saved document values`,
      list,
      titles: [title],
      legacy: true
    };
  }

  private nonEmptyRows(list: 'A' | 'B' | 'C', rowCount: number): number[] {
    const rows: number[] = [];
    for (let row = 1; row <= rowCount; row++) {
      if (this.rowHasValue(list, row))
        rows.push(row);
    }
    return rows;
  }

  private rowHasValue(list: 'A' | 'B' | 'C', row: number): boolean {
    return ['DocumentTitle', 'IssuingAuthority', 'DocumentNumber', 'DocumentExpirationDate']
      .some(suffix => !!this.value(`${list}${row}${suffix}`));
  }

  private setDocumentTitles(list: 'A' | 'B' | 'C', titles: string[], rowCount: number): void {
    for (let row = 1; row <= rowCount; row++) {
      const title = titles[row - 1] || '';
      const currentTitle = this.value(`${list}${row}DocumentTitle`);
      this.setValue(`${list}${row}DocumentTitle`, title);
      if (currentTitle && currentTitle !== title) {
        this.setValue(`${list}${row}IssuingAuthority`, '');
        this.setValue(`${list}${row}DocumentNumber`, '');
        this.setValue(`${list}${row}DocumentExpirationDate`, '');
      }
    }
  }

  private clearRows(list: 'A' | 'B' | 'C', rowCount: number): void {
    for (let row = 1; row <= rowCount; row++) {
      ['DocumentTitle', 'IssuingAuthority', 'DocumentNumber', 'DocumentExpirationDate']
        .forEach(suffix => this.setValue(`${list}${row}${suffix}`, ''));
    }
  }

  private applyDocumentValidators(): void {
    const activeFieldNames = new Set<string>();
    (['A', 'B', 'C'] as const).forEach(list => {
      if (this.isManualRow(list))
        return;
      this.activeRows(list).forEach(row => {
        activeFieldNames.add(`${list}${row}DocumentTitle`);
        activeFieldNames.add(`${list}${row}IssuingAuthority`);
      });
    });

    if (!this.primaryDocument)
      activeFieldNames.add('A1DocumentTitle');
    if (this.usesListBAndC && !this.listCDocument)
      activeFieldNames.add('C1DocumentTitle');

    this.documentFieldNames().forEach(fieldName => {
      const control = this.control(fieldName);
      if (!control)
        return;
      control.clearValidators();
      if (activeFieldNames.has(fieldName))
        control.setValidators(Validators.required);
      control.updateValueAndValidity({ emitEvent: false });
    });
  }

  private documentFieldNames(): string[] {
    const names: string[] = [];
    ['A1', 'A2', 'A3', 'B1', 'C1'].forEach(prefix => {
      ['DocumentTitle', 'IssuingAuthority', 'DocumentNumber', 'DocumentExpirationDate']
        .forEach(suffix => names.push(`${prefix}${suffix}`));
    });
    return names;
  }

  private control(fieldName: string) {
    const name = this.controlName(fieldName);
    return name ? this.form.get(name) : null;
  }

  private value(fieldName: string): string {
    return this.control(fieldName)?.value || '';
  }

  private setValue(fieldName: string, value: string): void {
    this.control(fieldName)?.setValue(value);
  }
}
